import { Navigate, Outlet, useLocation, useSearchParams } from 'react-router-dom';

import { Stepper, type StepperStep } from '@/components/ui';

import {
  ORDER_STEPS,
  firstIncompleteStep,
  isStepReachable,
  readOrderUrlState,
  stepByPath,
  stepUrl,
  type OrderStepDefinition,
  type OrderUrlState,
} from './steps';
import type { OrderStepContext } from './useOrderStep';

/**
 * Layout for /order/*. Owns the stepper and the reachability guard.
 *
 * There is no `useState` in this component and there must never be one: the
 * current step comes from `useLocation().pathname`, the ids come from
 * `useSearchParams()`, and both survive a refresh. See the long note at the top of
 * ./steps.ts for the full persistence rationale.
 *
 * The guard is intentionally URL-only for now (does the URL carry the required
 * ids?). Once the uploads/quotes endpoints exist it gains a second half: fetch
 * those ids and confirm the server agrees they are in a state that permits this
 * step — a `?q=` pointing at an expired quote must land the customer back on
 * /order/review, not on a payment page that will 409.
 */
export function OrderLayout() {
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const urlState: OrderUrlState = readOrderUrlState(searchParams);
  const current: OrderStepDefinition | undefined = stepByPath(location.pathname);

  // Unknown /order/* path: send them to the start of the flow.
  if (!current) {
    return <Navigate to={stepUrl('basket', urlState)} replace />;
  }

  // Deep link with missing prerequisites: redirect rather than render broken.
  // `replace` keeps the dead URL out of history so Back does not bounce.
  if (!isStepReachable(current, urlState)) {
    const target = firstIncompleteStep(urlState);
    return <Navigate to={stepUrl(target.id, urlState)} replace />;
  }

  const currentIndex = ORDER_STEPS.findIndex((step) => step.id === current.id);

  const steps: StepperStep[] = ORDER_STEPS.map((step, index) => {
    const reachable = isStepReachable(step, urlState);
    const complete = index < currentIndex && reachable;
    return {
      id: step.id,
      label: step.label,
      complete,
      // Only completed steps get a link — see Stepper.
      ...(complete ? { to: stepUrl(step.id, urlState) } : {}),
    };
  });

  const context: OrderStepContext = {
    urlState,
    step: current,
    currentIndex,
    stepper: <Stepper steps={steps} currentIndex={currentIndex} />,
  };

  return <Outlet context={context} />;
}
